import { gql, useMutation } from '@apollo/client'
import { pipe } from 'ramda'
import { useNavigate, useParams } from '@reach/router'
import { useTranslation } from 'react-i18next'

import { cleanRepliesForApi } from '../../../../utils/helpers/cleanRepliesRequest'
import { formsClient } from '../../../../utils/apollo/api'
import { getInvite, getPlaceId } from '../_helpers/queryParams'
import { getRepliesFromCategories } from '../../../../utils/helpers/dynamicInputs'
import { useAlertModal } from '../../../../utils/routes/modals/modalsActions'
import { useForm } from '../_load/FormLoader'
import { useNotification } from '@btime/react-messager'
import { useImperativeComponents } from '../../../../containers/dynamic/form/replies/context/ImperativeComponentsContext'
import { indexOfFirstInvalidReply } from '../../../serviceOrders/executionV2/_helpers/saveServiceOrder'

const MUTATION = gql`
  mutation InsertFormReply($input: FormReplyInput) {
    insertFormReply(input: $input) {
      id
      shareCode
    }
  }
`

const cleanRequest = pipe(getRepliesFromCategories, cleanRepliesForApi)

export const useSaveFormReply = () => {
  const { t } = useTranslation('listForms')
  const form = useForm()
  const placeId = getPlaceId()
  const invite = getInvite()
  const navigate = useNavigate()
  const [request, state] = useMutation(MUTATION, { client: formsClient })
  const openAlert = useAlertModal()
  const notify = useNotification()
  const { shareCode: formShareCode } = useParams()
  const { getRepliesValue, setFocus } = useImperativeComponents()

  const shouldBeRedirectToExecution = form.redirectToExecution
  const titleMessage = form.configuration?.titleMessage
  const bodyMessage = form.configuration?.bodyMessage

  const handleRequest = () => {
    const replies = getRepliesValue()
    const firstInvalidReplyIndex = indexOfFirstInvalidReply(replies)

    if (firstInvalidReplyIndex) {
      setFocus(firstInvalidReplyIndex?.questionId)
      return openAlert(t('requiredAnswersMissing'), t('requiredAnswersMissingMessage'), {
        icon: 'BsInfoCircle'
      })
    }

    const input = { formId: form.id, placeId, replies: cleanRequest(replies), ...(invite ? { invite } : {}) }

    return request({ variables: { input } }).then(resp => {
      if (!resp.errors) {
        if (shouldBeRedirectToExecution) {
          notify.success({
            title: titleMessage ?? t('successTitle'),
            message: bodyMessage ?? t('successBody') + '.'
          })
          navigate(`/forms/${formShareCode}/execution`)
        } else {
          navigate(`/forms/successfully-executed/${resp.data?.insertFormReply?.shareCode}`)
        }
      }
    })
  }

  return [handleRequest, state]
}
